import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import authService from "../api/authClient";
import { getUserProfile } from "../services/api";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

const initialUserState = {
  id: null,
  email: null,
  username: null,
  fullName: null,
  profilePicture: null,
  isProfileComplete: false,
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(initialUserState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFullUserSession = useCallback(async () => {
    try {
      const identity = await authService.getCurrentUser();
      if (!identity || !identity.id) {
        throw new Error("No valid user identity from token.");
      }
      let userState = { ...initialUserState, ...identity };
      try {
        const detailedProfile = await getUserProfile();
        userState = {
          ...userState,
          fullName: detailedProfile.full_name,
          profilePicture: detailedProfile.profile_picture,
          isProfileComplete: detailedProfile.is_complete,
        };
      } catch (profileError) {
        if (
          profileError.status === 404 ||
          profileError.response?.status === 404
        ) {
          console.log(
            "[AuthContext] User has identity but no profile record (404). Profile is incomplete."
          );
        } else {
          console.error("Could not fetch detailed profile:", profileError);
          setError("Could not load full profile details.");
        }
      }
      setCurrentUser(userState);
      return userState;
    } catch (err) {
      setCurrentUser(initialUserState);
      return null;
    }
  }, []);

  useEffect(() => {
    const loadInitialUser = async () => {
      setLoading(true);
      if (authService.isAuthenticated()) {
        await fetchFullUserSession();
      }
      setLoading(false);
    };
    const handleForceLogout = () => {
      console.log(
        "[AuthContext] Forced logout event detected. Clearing state."
      );
      setCurrentUser(initialUserState);
    };
    window.addEventListener("forceLogout", handleForceLogout);
    loadInitialUser();
    return () => {
      window.removeEventListener("forceLogout", handleForceLogout);
    };
  }, [fetchFullUserSession]);

  const login = useCallback(
    async (email, password) => {
      setLoading(true);
      setError(null);
      try {
        await authService.login(email, password);
        await fetchFullUserSession();
      } catch (err) {
        setError(err.response?.data?.detail || err.message || "Login failed");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchFullUserSession]
  );

  const logout = useCallback(async () => {
    await authService.logout();
    setCurrentUser(initialUserState);
  }, []);

  const register = authService.register;

  // --- START OF FIX ---
  // The isAuthenticated function is very useful for quick, synchronous checks.
  // It should be included in the context.
  const isAuthenticated = authService.isAuthenticated;

  const value = useMemo(
    () => ({
      currentUser,
      isProfileComplete: currentUser.isProfileComplete,
      loading,
      error,
      login,
      logout,
      register,
      isAuthenticated, // <-- ADD THIS LINE
      fetchUserProfile: fetchFullUserSession,
    }),
    [
      currentUser,
      loading,
      error,
      login,
      logout,
      register,
      isAuthenticated,
      fetchFullUserSession,
    ]
  );
  // --- END OF FIX ---

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
